import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { CultureCode, CurrencyCode, useEventLocaleCulture, useEventLocaleCurrency } from '@shared/hooks/useGeoLocale';
import { useResolvedCashFundLocale } from '@shared/utils/currency/useResolvedCashFundLocale';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useGetInternationalRegistryCurrencyQuery } from './useInternationalRegistryExperience.graphql.generated';
import { useIsInternationalRegistryRoute } from './useIsInternationalRegistryRoute';

export type InternationalRegistryExperience = Readonly<{
  isInternationalRegistry: boolean;
  loading: boolean;
}>;

export type InternationalExternalRegistries = Readonly<{
  showUkRetailers: boolean;
  linkOnlyExternalRegistries: boolean;
  loading: boolean;
}>;

/**
 * Shared base: resolves the raw signals the public hooks need — the lean flag,
 * the event locale culture, and (for eligibility) the currency-derived supported
 * (country, currency) pair — exactly once, so consumers don't re-run the same
 * underlying hooks.
 */
const useInternationalRegistryBase = () => {
  const [isLeanInternationalRegistryEnabled, isLeanFlagLoading] = useIsFeatureEnabled('leanInternationalRegistryEnabled');
  const eventLocaleCulture = useEventLocaleCulture();
  const eventLocaleCurrency = useEventLocaleCurrency();
  const { supported, loading: localeLoading } = useResolvedCashFundLocale(eventLocaleCurrency);
  const { eventHandle } = useEventInfo();

  const isInternationalEvent = eventLocaleCulture !== undefined && eventLocaleCulture !== CultureCode.US;
  const isCandidate = isLeanInternationalRegistryEnabled && isInternationalEvent;

  const { data: registryData, loading: registryLoading } = useGetInternationalRegistryCurrencyQuery({
    variables: { name: eventHandle ?? '' },
    skip: !isCandidate || !eventHandle,
    batchMode: 'fast',
    fetchPolicy: 'cache-first',
  });
  const disableIntlRegistry = Boolean(registryData?.eventByName?.registry?.disableIntlRegistry);

  return {
    eventLocaleCulture,
    isInternationalRegistry: isCandidate && supported !== null && !registryLoading && !disableIntlRegistry,
    loading: isInternationalEvent && (isLeanFlagLoading || (isCandidate && (localeLoading || registryLoading))),
  };
};

/**
 * Decides whether the event gets the lean international registry experience
 * (cash-fund-focused, no US catalog). True when the flag is on, the event's
 * creation locale is international, and the event-locale-derived currency is a
 * BE-supported cash fund currency.
 */
export const useInternationalRegistryExperience = (): InternationalRegistryExperience => {
  const { isInternationalRegistry, loading } = useInternationalRegistryBase();
  return { isInternationalRegistry, loading };
};

/**
 * External registry offering for the lean international experience, scoped to the
 * ACTIVE route (`/registry/intl`), not event eligibility. On the lean route every
 * connection is restricted to connect-by-URL (the transfer dialog forces the link
 * action; sync/transfer never surface), and UK events additionally get the UK
 * retailer tiles (uk-cambium BE section) as link entry points. On the US route
 * every flag is false, so the US experience — including the Amplitude-geo-targeted
 * Cambium UK offering, which keys off its own feature flag — is unaffected even for
 * an international-eligible account. `loading` reflects the UK-retailer resolution
 * and is only relevant on the lean route, where the transfer funnel must wait for
 * it before capturing its start screen.
 */
export const useInternationalExternalRegistries = (): InternationalExternalRegistries => {
  const { eventLocaleCulture } = useInternationalRegistryBase();
  const isInternationalRoute = useIsInternationalRegistryRoute();
  const { eventHandle } = useEventInfo();

  const { data, loading: registryCurrencyLoading } = useGetInternationalRegistryCurrencyQuery({
    variables: { name: eventHandle ?? '' },
    skip: !isInternationalRoute || !eventHandle,
    batchMode: 'fast',
    fetchPolicy: 'cache-first',
  });
  const registryCurrencyCode = data?.eventByName?.registry?.currency?.code;

  const isUkEvent = registryCurrencyCode === CurrencyCode.GBP || eventLocaleCulture === CultureCode.GB;

  return {
    showUkRetailers: isInternationalRoute && isUkEvent,
    linkOnlyExternalRegistries: isInternationalRoute,
    loading: isInternationalRoute && registryCurrencyLoading,
  };
};
